// client/src/lib/dates.js
import { DateTime } from 'luxon';

/**
 * Parse various datetime formats into a Luxon DateTime.
 *
 * Supported inputs:
 * - ISO strings (e.g. 2026-01-01T10:00:00.000Z)
 * - MySQL DATETIME strings (e.g. 2026-01-01 10:00:00) — treated as UTC
 * - DATE-only strings (e.g. 2026-01-01) — treated as UTC midnight
 * - JS Date objects
 * - epoch seconds/millis (number or numeric string)
 */
export function parseAnyDateTime(value, { assumeZone = 'utc' } = {}) {
  if (value === null || value === undefined || value === '') return null;

  // JS Date
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value, { zone: assumeZone });
    return dt.isValid ? dt : null;
  }

  // epoch seconds/millis
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    const s = String(value);
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    const dt = (s.length === 10)
      ? DateTime.fromSeconds(n, { zone: 'utc' })
      : DateTime.fromMillis(n, { zone: 'utc' });
    return dt.isValid ? dt : null;
  }

  const s = String(value).trim();

  // ISO strings
  if (s.includes('T') || s.endsWith('Z') || /[+-]\d\d:\d\d$/.test(s)) {
    const dt = DateTime.fromISO(s, { setZone: true });
    return dt.isValid ? dt : null;
  }

  // MySQL DATETIME: "YYYY-MM-DD HH:mm:ss" (or without seconds)
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s)) {
    const dt = DateTime.fromSQL(s, { zone: assumeZone });
    return dt.isValid ? dt : null;
  }

  // DATE-only: "YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const dt = DateTime.fromISO(s, { zone: assumeZone });
    return dt.isValid ? dt : null;
  }

  // Fallback attempts
  const dtISO = DateTime.fromISO(s, { setZone: true });
  if (dtISO.isValid) return dtISO;

  const dtSQL = DateTime.fromSQL(s, { zone: assumeZone });
  if (dtSQL.isValid) return dtSQL;

  return null;
}

/** Format a datetime in a specific IANA zone (e.g. "Australia/Perth"). */
export function fmtInZone(value, zone, fmt = 'ccc d LLL yyyy, t') {
  const dt = parseAnyDateTime(value, { assumeZone: 'utc' });
  if (!dt) return '—';
  const z = zone || DateTime.local().zoneName; // fallback only if zone missing
  return dt.setZone(z).toFormat(fmt);
}

/** Format a datetime in the group's timezone; fallback to browser tz if missing. */
export function fmtGroup(value, groupTimezone, fmt = 'ccc d LLL yyyy, t') {
  return fmtInZone(value, groupTimezone, fmt);
}

/** Viewer-local formatting (use sparingly). */
export function fmtLocal(value, fmt = 'ccc d LLL yyyy, t') {
  const dt = parseAnyDateTime(value, { assumeZone: 'utc' });
  if (!dt) return '—';
  return dt.toLocal().toFormat(fmt);
}

/** Optional helper to show a timezone label (zone param wins, else browser tz). */
export function tzLabel(zone) {
  return zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Build a UTC ISO from local date+time strings in a given zone (for form submissions). */
export function toUTCFromLocalParts(dateStr, timeStr, zone) {
  const z = zone || DateTime.local().zoneName;
  const dt = DateTime.fromFormat(`${dateStr} ${timeStr}`, 'yyyy-LL-dd HH:mm', { zone: z });
  return dt.toUTC().toISO();
}

// Format a DATE-ONLY value (YYYY-MM-DD) as dd/MM/yyyy in the group's tz
export function formatDateAU(dateIso, groupTimezone) {
  if (!dateIso) return '—';
  // treat date-only as UTC midnight; then show in group tz
  const dt = parseAnyDateTime(String(dateIso), { assumeZone: 'utc' });
  if (!dt) return '—';
  const z = groupTimezone || DateTime.local().zoneName;
  return dt.setZone(z).toFormat('dd/LL/yyyy');
}
